import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Create context
const AiPageContext = createContext(null);

/**
 * Helper to check if two contexts are equivalent
 * @param {Object} context1 - First context object 
 * @param {Object} context2 - Second context object
 * @returns {boolean} - Whether they are equivalent
 */
const areContextsEqual = (context1, context2) => {
  // If both are null/undefined, they're equal
  if (!context1 && !context2) return true;
  // If only one is null/undefined, they're not equal
  if (!context1 || !context2) return false;

  if (context1.type !== context2.type) return false;
  if (context1.label !== context2.label) return false;

  // Consider data changes to be meaningful for context updates
  try {
    const d1 = JSON.stringify(context1.data || {});
    const d2 = JSON.stringify(context2.data || {});
    if (d1 !== d2) return false;
  } catch (_) {
    // If stringify fails, assume different
    return false;
  }

  return true;
};

/**
 * Provider component that manages and provides page context for the AI Command Palette
 */
export const AiPageContextProvider = ({ children }) => {
  const [currentAiPageContext, setCurrentAiPageContext] = useState(null);
  const location = useLocation();

  // Reset context on location change before paint to avoid races with page-level setters
  useLayoutEffect(() => {
    setCurrentAiPageContext(null);
  }, [location.pathname]);

  // Enhanced setContext function
  const setContext = useCallback((context) => {
    // Stable compare and early return to avoid loops
    if (areContextsEqual(context, currentAiPageContext)) return;

    setCurrentAiPageContext(context);
  }, [currentAiPageContext]);

  return (
    <AiPageContext.Provider value={{ currentAiPageContext, setCurrentAiPageContext: setContext }}>
      {children}
    </AiPageContext.Provider>
  );
};

/**
 * Hook to use the page context
 */
export const useAiPageContext = () => {
  const context = useContext(AiPageContext);
  if (!context) {
    throw new Error('useAiPageContext must be used within a AiPageContextProvider');
  }
  return context;
}; 